// TODO(J-H): Consider renaming this store if we generalize further.
import api from "@/api";
import { pause } from "@/api/utils";
import WebSocketService from "@/services/webSocket";
import { useAuthStore } from "@/stores/auth";
import { useMessengerStore } from "@/stores/messenger";
import { useUserStore } from "@/stores/user";
import { useToast } from "vue-toastification";

// Used to override session id during prototype demos.
const EZEE_HASH_SESSION_ID = "ezee_session_id";
// SessionStorage key for session id.
const EZEE_STORAGE_SESSION_ID = "ezee.wfb.session_id";

const REQUIRED_PERM = "view-wfb-chat-page";
const DEFAULT_RECONNECT_INTERVAL = 300;
const DEFAULT_MAX_RECONNECT_ATTEMPT = 5000;

// store last 20 payloads for now, this should be enough to prevent dupes since its always within same message stream
const DEDUPE_BUFFER_SIZE = 20;

export const useWfbChatStore = defineStore("wfb-chat", {
  state: () => ({
    // TODO(J-H): Will removing accessToken, refreshToken & orgToken in its current
    // form, with accessToken to be replaced with the user's auth token. Blocked
    // till details regarding token refresh (for the PV2 MCP) and the Auth0 migration
    // is done.
    authToken: null,
    accessToken: null,
    // NOTE: refreshToken & orgToken are unused.
    refreshToken: null,
    orgToken: null,
    /** @type {WebSocketService|null} */
    websocket: null,
    /** @type {"MANUAL_DISCONNECT"|"CONNECTING"|"CONNECTED"|"DISCONNECTED"|"FAILED"} */
    connectionStatus: "MANUAL_DISCONNECT",
    history: new Map(),
    curReconnectAttempts: 0,
    isStreaming: false,
    isStoppingResponse: false,
    recentMessagePayloads: [],
    isInitializing: false,

    // Config stuff
    reconnectInterval: DEFAULT_RECONNECT_INTERVAL,
    maxReconnectAttempts: DEFAULT_MAX_RECONNECT_ATTEMPT,
  }),
  getters: {
    // I am pretty sure that the final WFB agent isn't prototype-style client
    // provided session id.
    sessionId() {
      const params = new URLSearchParams(window.location.search);
      let sessionId = params.get(EZEE_HASH_SESSION_ID);
      if (sessionId) {
        window.sessionStorage.setItem(EZEE_STORAGE_SESSION_ID, sessionId);
        console.log("saved session ID in URL params:", sessionId);
        return sessionId;
      }
      sessionId = window.sessionStorage.getItem(EZEE_STORAGE_SESSION_ID);
      console.log("session ID in SessionStorage:", sessionId);
      // Like I dont think "" should be allowed.
      if (!sessionId) {
        sessionId = window.crypto.randomUUID();
        window.sessionStorage.setItem(EZEE_STORAGE_SESSION_ID, sessionId);
        console.log("generated new session ID:", sessionId);
      }
      return sessionId;
    },
    lastMessage() {
      // Yeah, despite Map being ordered. Theres no way to get the last elem directly.
      // If this becomes a problem, add additional array to track order.
      return Array.from(this.history.values()).at(-1);
    },
  },
  actions: {
    /** Initialize WFB Chat Store and begin connecting to AC. */
    async initialize() {
      // prevent duplicate initialization from occuring during await.
      if (this.isInitializing) return;
      this.isInitializing = true;

      try{
        const userStore = useUserStore();
        if (!userStore.hasPerm(REQUIRED_PERM)) {
          await this.cleanup();
          return;
        }
        if (this.connectionStatus !== "MANUAL_DISCONNECT") await this.cleanup();
        this.connectionStatus = "DISCONNECTED";
        // Returns once websocket binded, so still in CONNECTING state.
        await this._reconnect();
      } finally {
        this.isInitializing = false;
      }
    },

    /** Sets this.websocket to null after ensuring the websocket is closed. */
    _closeWebsocket() {
      if (this.websocket) {
        // Both funcs don't seem like they can error.
        this.websocket.close();
        this.websocket.removeAllListeners();
      }
      this.websocket = null;
    },

    /** Closes the AC connection and perform any other cleanup actions. */
    async cleanup() {
      this._closeWebsocket();
      this.$reset();
    },

    // Refer to frontend/src/views/messenger/components/MessageComposer.vue.
    async sendMessage(message, requestId, attachments = []) {
      // TODO: Replace this with unified attachment signing code.
      const { handleAttachmentsUpload } = useMessengerStore();

      // Use fake ID; history gets reset with real ID after refreshing anyways.
      const tempId = `temp-${window.crypto.randomUUID()}`;
      // Temporary message to display on frontend till next refresh since server
      // doesn't echo back user's message.
      this.history.set(tempId, {
        id: tempId,
        msg_type: "user",
        response: message,
        success: true,
        attachments: attachments.map((file) => ({
          filename: file.name,
          url: URL.createObjectURL(file),
        })),
      });

      const { transformedAttachments } =
        await handleAttachmentsUpload(attachments);

      const data = {
        version: "v1", // unused, but everything uses this value.
        session_id: this.sessionId,
        message, // must be non-empty, non-whitespace-only
        request_id: requestId,
        attachments: transformedAttachments,
        extras: {
          // Passed to WFB and eventually PV2 MCP to act on behalf of user.
          user_token: this.authToken,
        }
      };
      this.websocket.send(JSON.stringify(data));
    },

    // Refer to frontend/src/views/messenger/components/MessageComposer.vue.
    async stopResponse(requestId) {
      const stopResponseURL = `${import.meta.env.VITE_APP_AC_SERVER_HTTPS_URL}/api/agentchat/stop-response/${requestId}?token=${this.accessToken}`;
      const stopResponseRes = await fetch(stopResponseURL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ session_id: this.sessionId }),
      });
      if (stopResponseRes.ok) {
        this.isStoppingResponse = true;
      };
      return stopResponseRes;
    },

    async _failWithMsg(msg) {
      this.connectionStatus = "FAILED";
      const toast = useToast();
      toast.error(msg);
    },

    async _fetchAuthTokens() {
      const userStore = useUserStore();
      const authStore = useAuthStore();

      // If error occurs, considered fatal and should bubble all the way up.
      ({
        data: {
          access_token: this.accessToken,
          refresh_token: this.refreshToken,
          org_token: this.orgToken,
        },
      } = await api.messenger.getAuthTokensForAcServer(userStore.activeOrganization));
      this.authToken = (await authStore.getTokens()).accessToken;
    },

    /**
     * Only if in DISCONNECTED state, begin connecting. Returns once connection
     * process successfully starts. If disconnected then, websocket's event handler
     * triggers reconnection.
     */
    async _reconnect() {
      while (this.connectionStatus === "DISCONNECTED") {
        this.connectionStatus = "CONNECTING";
        this._closeWebsocket();

        try {
          await this._fetchAuthTokens();
        } catch (err) {
          console.error("error fetching auth tokens:", err);

          this.connectionStatus = "DISCONNECTED";
          await pause(this.reconnectInterval);
          continue;
        }

        if (this.connectionStatus !== "CONNECTING") {
          console.log("_reconnect -> aborted stale reconnect attempt");
          return;
        }

        // This is temporary cuz I need it to give up if there's no access token
        // anywhere. Long-term might make sense that setting this.accessToken
        // to null instead of throwing error can be used to signal failure.
        if (!this.accessToken) {
          console.error("no WFB access token available!");
          this._failWithMsg("Failed to authenticate, please refresh your page.")
          return;
        }

        const ws = api.wfb.getWfbWebSocketService(
          this.accessToken,
          this.sessionId
        );
        this._bindWebsocket(ws);
        ws.connect();
        // Prevent await race by ensuring there isn't a open websocket before this.
        this._closeWebsocket();
        this.websocket = ws;
      }
    },

    /** @param {WebSocketService} ws */
    _bindWebsocket(ws) {
      // Looking in WebSocketService, event val for connected/disconnected is useless.
      ws.on("connected", this._wsOnConnected);
      ws.on("disconnected", this._wsOnDisconnected);
      // This error is only emitted when the websocket is closed with non-1000 code.
      ws.on("error", this._wsOnError);
      ws.addListener(this._wsOnMessage);
    },

    _wsOnConnected() {
      console.log("connected");
      this.connectionStatus = "CONNECTED";
      this.curReconnectAttempts = 0;
    },

    _wsOnDisconnected() {
      console.log("disconnected while in state:", this.connectionStatus);

      if (this.connectionStatus === "MANUAL_DISCONNECT") return;
      else if (this.connectionStatus === "FAILED") return;
      else if (this.curReconnectAttempts >= this.maxReconnectAttempts) {
        this._failWithMsg("Unable to connect to Workflow Builder Agent Messenger.")
        return;
      }

      this.connectionStatus = "DISCONNECTED";
      this.curReconnectAttempts += 1;
      setTimeout(() => {
          console.log(
            `reconnect attempt: ${this.curReconnectAttempts}/${this.maxReconnectAttempts}`
          );

          this._reconnect();
        },
        this.reconnectInterval
      );
    },

    _wsOnError(evt) {
      console.error("server closed connection with fatal error", evt);
      // this._failWithMsg("Connection was closed unexpectedly, please refresh your page.")
      this.connectionStatus = "DISCONNECTED";
      // Don't close websocket here yet, else _wsOnDisconnected won't trigger.
    },

    _isDuplicateMessagePayload(payload) {
      // just compare the raw json payload as a string since it should be the perfect unique check
      // if its really just the same message but with two listeners/socket conns etc
      if (this.recentMessagePayloads.includes(payload)) {
        return true;
      }

      this.recentMessagePayloads.push(payload);

      while (this.recentMessagePayloads.length > DEDUPE_BUFFER_SIZE) {
        this.recentMessagePayloads.shift();
      }

      return false;
    },

    _wsOnMessage(data) {
      if (this._isDuplicateMessagePayload(data)) {
        console.warn("_wsOnMessage -> duplicate websocket payload ignored:", data);
        return;
      }

      const hist = JSON.parse(data);
      // console.log("message:", hist);

      // Use $patch here to batch all updates efficiently.
      // Map used to maximize update speed since streaming, doesn't need assumption
      // of last message being the one streamed.
      this.$patch((state) => {
        let isAnyStreaming = false;
        let isMessageDone = false;
        hist.forEach((msg) => {
          // Throw if errors occur:
          if (msg.msg_type === "system" && msg.success === false) {
            console.error("Access channel sent error message:", msg);
            this._failWithMsg("The server encountered an error, please refresh your page.")
          }
          // Ack seems to use same id as previous message, thus overriding it?
          if (msg.msg_type === "system") {
            console.log("Access channel system message:", msg);
            return;
          }

          if (msg.extras?.message?.streaming) {
            if (!state.history.has(msg.id)) {
              state.history.set(msg.id, msg);
            } else {
              // Update existing message.
              state.history.get(msg.id).response += msg.response;
            }

            if (msg.extras?.message?.more) {
              isAnyStreaming = true;
            } else {
              // Final message, update everything else too.
              const finalResponse = state.history.get(msg.id).response;
              state.history.set(msg.id, { ...msg, response: finalResponse });
              isMessageDone = true;
            }
          } else {
            // When creating a new connection, server replays history.
            state.history.set(msg.id, msg);
          }
        });
        if (isMessageDone) {
          this.isStoppingResponse = false;
        };
        this.isStreaming = isAnyStreaming;
      });
    },
  },
});
